/**
 * Hook: same display logs as /audits after search, permission, time,
 * compliance, type checks, and cash/units filters.
 */

import { useMemo } from 'react';
import { useAppSelector } from 'redux/hooks';
import { selectPermission, selectStaffId, selectUsername } from 'redux/global';
import { selectMetricTime } from 'pages/redux';
import { selectCustomers, selectUsers } from 'pages/Settings/redux';
import { useSettings } from 'pages/Settings/hooks';
import { useLogs } from './hooks';
import {
  selectAssociateElevatedAudits,
  selectAuditsTypeAttributeMode,
  selectAuditsViewAs,
  selectAuditsOnlyLogIds,
  selectAuditsStartLogId,
  selectAuditsEndLogId,
  selectComplianceAuditStaffId,
  selectIsComplianceAudit,
  selectLogSearch,
  selectLogSearchOptions,
  selectLogSearches,
  selectLogsChecked,
  selectLogsValueMode,
  selectSingleLogTime,
} from './redux';
import { buildCustomerMaps } from './logCustomer';
import { buildStaffMaps } from './logActor';
import {
  AuditsDisplayLogsParams,
  buildAuditsDisplayLogs,
} from './auditsDisplayLogs';
import type { AuditsTypeAttributeMode } from './logUtils';

export type UseAuditsDisplayLogsOptions = {
  /** Override Audits cash/units toggles (Reporting defaults match Audits defaults). */
  allItems?: boolean,
  cashIn?: boolean,
  cashOut?: boolean,
  /** Force time on/off; default = singleLogTime has a range (Audits time toggle). */
  useTimeRange?: boolean,
  /** Override checked types; default = Redux Audits checks. */
  checked?: string[],
};

/**
 * Returns the exact log set /audits would show (pre-pagination) for the
 * current Redux Audits filters + session permission.
 */
export const useAuditsDisplayLogs = (options: UseAuditsDisplayLogsOptions = {}) => {
  const permission = useAppSelector(selectPermission);
  const staffId = useAppSelector(selectStaffId);
  const username = useAppSelector(selectUsername);
  const auditsViewAs = useAppSelector(selectAuditsViewAs);
  const auditsOnlyLogIds = useAppSelector(selectAuditsOnlyLogIds);
  const auditsStartLogId = useAppSelector(selectAuditsStartLogId);
  const auditsEndLogId = useAppSelector(selectAuditsEndLogId);
  // Report Show logs: other party's identity, never above the signed-in permission.
  const effectivePermission = auditsViewAs
    ? Math.min(Number(permission) || 0, Number(auditsViewAs.permission) || 0)
    : (Number(permission) || 0);
  const effectiveStaffId = auditsViewAs ? auditsViewAs.staffId : staffId;
  const effectiveUsername = auditsViewAs ? auditsViewAs.username : username;
  const { users: settingsUsers } = useSettings();
  const usersFromStore = useAppSelector(selectUsers);
  const users = settingsUsers?.length ? settingsUsers : (usersFromStore || []);
  const customers = useAppSelector(selectCustomers);
  const logSearch = useAppSelector(selectLogSearch);
  const logSearchOptions = useAppSelector(selectLogSearchOptions);
  const logSearches = useAppSelector(selectLogSearches);
  const logsChecked = useAppSelector(selectLogsChecked);
  const logsValueMode = useAppSelector(selectLogsValueMode);
  const auditsTypeAttributeMode = useAppSelector(selectAuditsTypeAttributeMode) as AuditsTypeAttributeMode;
  const singleLogTime = useAppSelector(selectSingleLogTime);
  const metricT = useAppSelector(selectMetricTime);
  const associateElevatedAudits = useAppSelector(selectAssociateElevatedAudits);
  const isComplianceAuditFlag = useAppSelector(selectIsComplianceAudit);
  const complianceAuditStaffId = useAppSelector(selectComplianceAuditStaffId);

  const {
    logs,
    logGroups,
    deletedDepartmentNames,
    deletedInventoryItemNames,
    deletedInventoryGroupNames,
  } = useLogs();

  const checked = options.checked ?? (logsChecked || []);
  const valueMode = (logsValueMode === 'units' ? 'units' : 'value') as 'value' | 'units';
  // Match LogTable: time filter is on when singleLogTime is set (cleared when Time is off).
  const useTimeRange = options.useTimeRange ?? Boolean(
    singleLogTime?.startDate && singleLogTime?.endDate,
  );
  const { startDate, endDate } = (singleLogTime || metricT || {}) as {
    startDate?: string,
    endDate?: string,
  };

  // Same formula as LogTable (elevate unlocks higher-role view; compliance filter is separate).
  const isComplianceAudit = Boolean(
    isComplianceAuditFlag
    && useTimeRange
    && startDate
    && endDate
    && complianceAuditStaffId,
  );

  // Audits defaults: Non-Cash off (allItems true) unless elevate+units.
  const allItems = options.allItems ?? !(associateElevatedAudits && valueMode === 'units');
  const cashIn = options.cashIn ?? true;
  const cashOut = options.cashOut ?? true;

  const staffMaps = useMemo(() => buildStaffMaps(users), [users]);
  const customerMaps = useMemo(() => buildCustomerMaps(customers), [customers]);
  const departmentNames = useMemo(
    () => new Set(logGroups.Departments || []),
    [logGroups.Departments],
  );
  const searchArchives = useMemo(() => ({
    deletedTypes: deletedInventoryItemNames,
    deletedGroups: deletedInventoryGroupNames,
    deletedDepartments: deletedDepartmentNames,
  }), [deletedInventoryItemNames, deletedInventoryGroupNames, deletedDepartmentNames]);

  const params: AuditsDisplayLogsParams = {
    logs: logs || [],
    permission: effectivePermission,
    staffId: effectiveStaffId,
    username: effectiveUsername,
    users: users || [],
    logSearches: logSearches || [],
    logSearch: logSearch || '',
    logSearchOptions,
    staffMaps,
    customerMaps,
    departmentNames,
    searchArchives,
    checked,
    logGroups: logGroups as Record<string, string[]>,
    useTimeRange,
    startDate,
    endDate,
    valueMode,
    allItems,
    cashIn,
    cashOut,
    associateElevatedAudits: Boolean(associateElevatedAudits),
    isComplianceAudit,
    complianceAuditStaffId,
    onlyLogIds: auditsOnlyLogIds,
    startLogId: auditsStartLogId,
    endLogId: auditsEndLogId,
  };

  const displayLogs = useMemo(
    () => buildAuditsDisplayLogs(params),
    [
      logs,
      effectivePermission,
      effectiveStaffId,
      effectiveUsername,
      users,
      logSearches,
      logSearch,
      logSearchOptions,
      staffMaps,
      customerMaps,
      departmentNames,
      searchArchives,
      checked,
      logGroups,
      useTimeRange,
      startDate,
      endDate,
      valueMode,
      allItems,
      cashIn,
      cashOut,
      associateElevatedAudits,
      isComplianceAudit,
      complianceAuditStaffId,
      auditsOnlyLogIds,
      auditsStartLogId,
      auditsEndLogId,
    ],
  );

  return {
    displayLogs,
    logGroups,
    checked,
    valueMode,
    attributeMode: auditsTypeAttributeMode,
    useTimeRange,
    startDate,
    endDate,
  };
};

export default useAuditsDisplayLogs;
